import { toast } from "@devhop/ui";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type AddShopProductClaimAttachmentDTO,
  type BulkCreateExchangeRatesDTO,
  type CreatePurchaseOrderDTO,
  type CreateShopBranchDTO,
  type CreateShopPayoutRequestDTO,
  type CreateShopProductClaimDTO,
  type CreateShopPromotionDTO,
  type CreateSupplierDTO,
  type ExchangeRateHistoryQueryDTO,
  type InventoryAssignLocationDTO,
  type InventoryByLocationQueryDTO,
  type InventoryMovementTypeDTO,
  InventoryMovementTypeSchema,
  type PosCheckoutDTO,
  type PosCheckoutPreviewDTO,
  type PosCreateHoldDTO,
  type PosHoldConfirmDTO,
  type PosSaleAddPaymentsDTO,
  type PosSalesByCurrencyReportQueryDTO,
  type PosSalesExtendedReportQueryDTO,
  type PosSalesListQueryDTO,
  type ProvisionPosSellableProductDTO,
  type PublishMarketplaceListingDTO,
  type PurchaseOrderGoodsReceiptDTO,
  type PurchaseOrderListQueryDTO,
  type SellableCatalogQueryDTO,
  type SetShopProductClaimStatusDTO,
  type ShopInventoryAdjustDTO,
  type ShopInventoryGoodsReceiptDTO,
  type ShopInventoryListQueryDTO,
  type ShopInventoryLotLabelsQueryDTO,
  type ShopInventoryMovementsQueryDTO,
  type ShopInventoryReconcileQueryDTO,
  type ShopInventoryReleaseDTO,
  type ShopInventoryReserveDTO,
  type ShopInventorySerialsQueryDTO,
  type ShopMarketplaceListQueryDTO,
  type ShopPayoutRequestsQueryDTO,
  type ShopProductClaimListQueryDTO,
  type ShopPromotionListQueryDTO,
  type ShopSettlementPeriodsQueryDTO,
  type ShopStockTransferDTO,
  type ShopWarrantiesExpiringQueryDTO,
  type SupplierListQueryDTO,
  type UpdatePurchaseOrderDTO,
  type UpdateShopBranchDTO,
  type UpdateShopProductClaimDraftDTO,
  type UpdateShopProductSkuDTO,
  type UpdateShopPromotionDTO,
  type UpdateShopSettingsDTO,
  type UpdateSupplierDTO,
  type WarehouseEmptyLocationsReportQueryDTO,
  type WarehouseReceiveByLocationReportQueryDTO,
  type WarehouseUnlocatedReportQueryDTO,
} from "@/modules/merchant/domain/contracts";
import type {
  CreateShopShiftAssignmentDTO,
  ShiftAssignmentsListQueryDTO,
  ShiftStaffOptionsQueryDTO,
  UpdateShopShiftAssignmentDTO,
} from "@/modules/merchant/domain/shift-assignments.contracts";
import type {
  CreateShopShiftSwapRequestDTO,
  ShopShiftSwapRequestsListQueryDTO,
} from "@/modules/merchant/domain/shift-swap.contracts";
import type { ShopStaffListQueryDTO } from "@/shared/contracts/base";
import { useMerchantShopsStore } from "../store/merchant-shops-store";
import {
  merchantApi,
  shopBranchesApi,
  shopCatalogApi,
  shopInventoryApi,
  shopMarketplaceApi,
  shopPosApi,
  shopProductClaimsApi,
  shopPromotionsApi,
  shopPurchaseOrdersApi,
  shopReportsApi,
  shopRolesApi,
  shopServicesApi,
  shopSettlementApi,
  shopShiftAssignmentsApi,
  shopShiftSwapRequestsApi,
  shopStaffApi,
  shopSuppliersApi,
  shopWarrantiesApi,
} from "./client";

export const shopRolesKeys = {
  all: (shopId: string) => ["shops", shopId, "roles"] as const,
  list: (shopId: string) => [...shopRolesKeys.all(shopId), "list"] as const,
  detail: (shopId: string, roleId: string) =>
    [...shopRolesKeys.all(shopId), "detail", roleId] as const,
};

export function useShopRolesQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopRolesKeys.list(shopId ?? ""),
    queryFn: async () => {
      if (!shopId) throw new Error("shopId required");
      const r = await shopRolesApi.list(shopId);
      return r.items;
    },
    enabled: !!shopId,
  });
}

export function useShopRoleQuery(
  shopId: string | undefined,
  roleId: string | undefined,
) {
  return useQuery({
    queryKey: shopRolesKeys.detail(shopId ?? "", roleId ?? ""),
    queryFn: async () => {
      if (!shopId || !roleId) throw new Error("shopId and roleId required");
      return shopRolesApi.get(shopId, roleId);
    },
    enabled: !!shopId && !!roleId,
  });
}

export function useSeedDefaultRoles(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!shopId) throw new Error("shopId required");
      return shopRolesApi.seedDefault(shopId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopRolesKeys.all(shopId) });
      toast.success("ສ້າງບົດບາດເລີ່ມຕົ້ນສຳເລັດ");
    },
    onError: () => toast.error("ສ້າງຄ່າເລີ່ມຕົ້ນລົ້ມເຫຼວ"),
  });
}

export function useDeleteShopRole(shopId: string | undefined) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: async (roleId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopRolesApi.delete(shopId, roleId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopRolesKeys.all(shopId) });
    },
  });

  const run = (roleId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(roleId, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}

// ---------------------------------------------------------------------------
// Shop branches
// ---------------------------------------------------------------------------

export const shopBranchesKeys = {
  all: (shopId: string) => ["shops", shopId, "branches"] as const,
  list: (shopId: string) => [...shopBranchesKeys.all(shopId), "list"] as const,
};

export function useShopBranchesListQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopBranchesKeys.list(shopId ?? ""),
    queryFn: async () => {
      if (!shopId) throw new Error("shopId required");
      const r = await shopBranchesApi.list(shopId);
      return r.items;
    },
    enabled: !!shopId,
  });
}

export function useCreateShopBranch(shopId: string | undefined) {
  const qc = useQueryClient();
  const refetchShops = useMerchantShopsStore((s) => s.fetchShops);
  return useMutation({
    mutationFn: async (data: CreateShopBranchDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopBranchesApi.create(shopId, data);
    },
    onSuccess: () => {
      if (shopId)
        qc.invalidateQueries({ queryKey: shopBranchesKeys.all(shopId) });
      void refetchShops();
      toast.success("ເພີ່ມສາຂາສຳເລັດ");
    },
    onError: () => toast.error("ເພີ່ມສາຂາລົ້ມເຫຼວ"),
  });
}

export function useUpdateShopBranch(shopId: string | undefined) {
  const qc = useQueryClient();
  const refetchShops = useMerchantShopsStore((s) => s.fetchShops);
  return useMutation({
    mutationFn: async (args: {
      branchId: string;
      data: UpdateShopBranchDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopBranchesApi.update(shopId, args.branchId, args.data);
    },
    onSuccess: () => {
      if (shopId)
        qc.invalidateQueries({ queryKey: shopBranchesKeys.all(shopId) });
      void refetchShops();
      toast.success("ບັນທຶກສາຂາສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກສາຂາລົ້ມເຫຼວ"),
  });
}

// ---------------------------------------------------------------------------
// Shift assignments (ປະຕິທິນກະ — S1)
// ---------------------------------------------------------------------------

export const shopShiftAssignmentsKeys = {
  all: (shopId: string) => ["shops", shopId, "shift-assignments"] as const,
  list: (shopId: string, q: ShiftAssignmentsListQueryDTO) =>
    [
      ...shopShiftAssignmentsKeys.all(shopId),
      "list",
      q.branchId,
      q.from,
      q.to,
      q.shopStaffId ?? "",
    ] as const,
  staffOptions: (shopId: string, q: ShiftStaffOptionsQueryDTO) =>
    [
      ...shopShiftAssignmentsKeys.all(shopId),
      "staff-options",
      q.branchId,
      q.forPosClock ?? "",
    ] as const,
  reportSummary: (shopId: string, q: ShiftAssignmentsListQueryDTO) =>
    [
      ...shopShiftAssignmentsKeys.all(shopId),
      "report-summary",
      q.branchId,
      q.from,
      q.to,
      q.shopStaffId ?? "",
    ] as const,
};

export function useShopShiftAssignmentsListQuery(
  shopId: string | undefined,
  query: ShiftAssignmentsListQueryDTO | null,
) {
  return useQuery({
    queryKey:
      shopId && query
        ? shopShiftAssignmentsKeys.list(shopId, query)
        : ([
            "shops",
            shopId ?? "",
            "shift-assignments",
            "list",
            "idle",
          ] as const),
    queryFn: async () => {
      if (!shopId || !query) throw new Error("shopId and query required");
      return shopShiftAssignmentsApi.list(shopId, query);
    },
    enabled: !!shopId && !!query,
  });
}

export function useShopShiftStaffOptionsQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  opts?: { forPosClock?: boolean },
) {
  const q: ShiftStaffOptionsQueryDTO | null =
    shopId && branchId
      ? {
          branchId,
          forPosClock: opts?.forPosClock ? "1" : undefined,
        }
      : null;
  return useQuery({
    queryKey:
      shopId && q
        ? shopShiftAssignmentsKeys.staffOptions(shopId, q)
        : ([
            "shops",
            shopId ?? "",
            "shift-assignments",
            "staff-options",
            "idle",
          ] as const),
    queryFn: async () => {
      if (!shopId || !q) throw new Error("shopId and branchId required");
      return shopShiftAssignmentsApi.staffOptions(shopId, q);
    },
    enabled: !!shopId && !!q,
  });
}

export function useCreateShopShiftAssignment(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateShopShiftAssignmentDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftAssignmentsApi.create(shopId, data);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ມອບໝາຍກະສຳເລັດ");
    },
    onError: () => toast.error("ມອບໝາຍກະລົ້ມເຫຼວ"),
  });
}

export function useUpdateShopShiftAssignment(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      assignmentId: string;
      data: UpdateShopShiftAssignmentDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftAssignmentsApi.update(
        shopId,
        args.assignmentId,
        args.data,
      );
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ອັບເດດສຳເລັດ");
    },
    onError: () => toast.error("ອັບເດດລົ້ມເຫຼວ"),
  });
}

export function useShopShiftReportSummaryQuery(
  shopId: string | undefined,
  query: ShiftAssignmentsListQueryDTO | null,
) {
  return useQuery({
    queryKey:
      shopId && query
        ? shopShiftAssignmentsKeys.reportSummary(shopId, query)
        : ([
            "shops",
            shopId ?? "",
            "shift-assignments",
            "report",
            "idle",
          ] as const),
    queryFn: async () => {
      if (!shopId || !query) throw new Error("shopId and query required");
      return shopShiftAssignmentsApi.reportSummary(shopId, query);
    },
    enabled: !!shopId && !!query,
  });
}

export function useOpenShopShiftMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      branchId: string;
      shopStaffId: string;
      workDate?: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftAssignmentsApi.open(shopId, args);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ເປີດກະສຳເລັດ");
    },
    onError: () => toast.error("ເປີດກະລົ້ມເຫຼວ"),
  });
}

export function useClockInShopShiftAssignment(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftAssignmentsApi.clockIn(shopId, assignmentId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ບັນທຶກເວລາເຂົ້າສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກເວລາເຂົ້າລົ້ມເຫຼວ"),
  });
}

export function useClockOutShopShiftAssignment(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assignmentId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftAssignmentsApi.clockOut(shopId, assignmentId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ບັນທຶກເວລາອອກສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກເວລາອອກລົ້ມເຫຼວ"),
  });
}

// ---------------------------------------------------------------------------
// Shift swap requests (S3)
// ---------------------------------------------------------------------------

export const shopShiftSwapRequestsKeys = {
  all: (shopId: string) => ["shops", shopId, "shift-swap-requests"] as const,
  list: (shopId: string, q: ShopShiftSwapRequestsListQueryDTO) =>
    [
      ...shopShiftSwapRequestsKeys.all(shopId),
      "list",
      q.branchId,
      q.status ?? "",
    ] as const,
};

export function useShopShiftSwapRequestsListQuery(
  shopId: string | undefined,
  query: ShopShiftSwapRequestsListQueryDTO | null,
) {
  return useQuery({
    queryKey:
      shopId && query
        ? shopShiftSwapRequestsKeys.list(shopId, query)
        : ([
            "shops",
            shopId ?? "",
            "shift-swap-requests",
            "list",
            "idle",
          ] as const),
    queryFn: async () => {
      if (!shopId || !query) throw new Error("shopId and query required");
      return shopShiftSwapRequestsApi.list(shopId, query);
    },
    enabled: !!shopId && !!query,
  });
}

export function useCreateShopShiftSwapRequest(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateShopShiftSwapRequestDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftSwapRequestsApi.create(shopId, data);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftSwapRequestsKeys.all(shopId),
        });
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ສົ່ງຄຳຂໍແລກກະແລ້ວ");
    },
    onError: () => toast.error("ສົ່ງຄຳຂໍລົ້ມເຫຼວ"),
  });
}

export function useApproveShopShiftSwapRequest(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftSwapRequestsApi.approve(shopId, requestId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftSwapRequestsKeys.all(shopId),
        });
        qc.invalidateQueries({
          queryKey: shopShiftAssignmentsKeys.all(shopId),
        });
      }
      toast.success("ອະນຸມັດແລກກະແລ້ວ");
    },
    onError: () => toast.error("ອະນຸມັດລົ້ມເຫຼວ"),
  });
}

export function useRejectShopShiftSwapRequest(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftSwapRequestsApi.reject(shopId, requestId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftSwapRequestsKeys.all(shopId),
        });
      }
      toast.success("ປະຕິເສດຄຳຂໍແລ້ວ");
    },
    onError: () => toast.error("ປະຕິເສດລົ້ມເຫຼວ"),
  });
}

export function useCancelShopShiftSwapRequest(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopShiftSwapRequestsApi.cancel(shopId, requestId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopShiftSwapRequestsKeys.all(shopId),
        });
      }
      toast.success("ຍົກເລີກຄຳຂໍແລ້ວ");
    },
    onError: () => toast.error("ຍົກເລີກລົ້ມເຫຼວ"),
  });
}

export function usePatchShopSettings(shopId: string | undefined) {
  const refetchShops = useMerchantShopsStore((s) => s.fetchShops);
  return useMutation({
    mutationFn: async (body: UpdateShopSettingsDTO) => {
      if (!shopId) throw new Error("shopId required");
      return merchantApi.patchShopSettings(shopId, body);
    },
    onSuccess: () => {
      void refetchShops();
      toast.success("ບັນທຶກຕັ້ງຄ່າຮ້ານສຳເລັດ");
    },
    // ສະແດງຂໍ້ຄວາມຈິງຈາກ server (ເຊັ່ນ "ມີ SKU ທີ່ສະກຸນບໍ່ກົງກັບຮ້ານ…") ແທນຂໍ້ຄວາມທົ່ວໄປ
    onError: (err) =>
      toast.error(
        err instanceof Error && err.message ? err.message : "ບັນທຶກຕັ້ງຄ່າຮ້ານລົ້ມເຫຼວ",
      ),
  });
}

export const exchangeRatesKeys = {
  all: (shopId: string) => ["shops", shopId, "exchange-rates"] as const,
  list: (shopId: string, asOf?: string) =>
    [...exchangeRatesKeys.all(shopId), "list", asOf ?? ""] as const,
  history: (shopId: string, q: Partial<ExchangeRateHistoryQueryDTO>) =>
    [...exchangeRatesKeys.all(shopId), "history", q] as const,
};

export function useExchangeRatesQuery(
  shopId: string | undefined,
  asOf?: string,
) {
  return useQuery({
    queryKey: exchangeRatesKeys.list(shopId ?? "", asOf),
    queryFn: () => merchantApi.listExchangeRates(shopId!, asOf),
    enabled: !!shopId,
  });
}

export function useExchangeRateHistoryQuery(
  shopId: string | undefined,
  q: Partial<ExchangeRateHistoryQueryDTO>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: exchangeRatesKeys.history(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return merchantApi.listExchangeRateHistory(shopId, q);
    },
    enabled: Boolean(shopId) && enabled,
  });
}

export function useBulkCreateExchangeRatesMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BulkCreateExchangeRatesDTO) => {
      if (!shopId) throw new Error("shopId required");
      return merchantApi.bulkCreateExchangeRates(shopId, body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: exchangeRatesKeys.all(shopId ?? ""),
      });
      toast.success("ບັນທຶກອັດຕາທັງໝົດສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກອັດຕາລົ້ມເຫຼວ"),
  });
}

// ---------------------------------------------------------------------------
// Shop staff (พนักงานในร้าน)
// ---------------------------------------------------------------------------

export const shopStaffKeys = {
  all: (shopId: string) => ["shops", shopId, "staff"] as const,
  list: (shopId: string, q: Partial<ShopStaffListQueryDTO>) =>
    [...shopStaffKeys.all(shopId), "list", q] as const,
  detail: (shopId: string, staffId: string) =>
    [...shopStaffKeys.all(shopId), "detail", staffId] as const,
};

export function useShopStaffQuery(
  shopId: string | undefined,
  query: Partial<ShopStaffListQueryDTO> = {},
) {
  const q: ShopStaffListQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    expandAssignments: query.expandAssignments,
  };
  return useQuery({
    queryKey: shopStaffKeys.list(shopId ?? "", q),
    queryFn: () => shopStaffApi.list(shopId!, q),
    enabled: !!shopId,
  });
}

export function useShopStaffDetailQuery(
  shopId: string | undefined,
  staffId: string | undefined,
) {
  return useQuery({
    queryKey: shopStaffKeys.detail(shopId ?? "", staffId ?? ""),
    queryFn: async () => {
      if (!shopId || !staffId) throw new Error("shopId and staffId required");
      return shopStaffApi.get(shopId, staffId);
    },
    enabled: !!shopId && !!staffId,
  });
}

export function useCreateShopStaff(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      userId: string;
      shopRoleId?: string | null;
      branchId?: string | null;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopStaffApi.create(shopId, data);
    },
    onSuccess: (_, __, ctx) => {
      if (shopId) qc.invalidateQueries({ queryKey: shopStaffKeys.all(shopId) });
      toast.success("ເພີ່ມພະນັກງານສຳເລັດ");
    },
    onError: () => toast.error("ເພີ່ມພະນັກງານລົ້ມເຫຼວ"),
  });
}

export function useUpdateShopStaff(
  shopId: string | undefined,
  staffId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      shopRoleId?: string | null;
      branchId?: string | null;
      isActive?: boolean;
    }) => {
      if (!shopId || !staffId) throw new Error("shopId and staffId required");
      return shopStaffApi.update(shopId, staffId, data);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopStaffKeys.all(shopId) });
      if (shopId && staffId)
        qc.invalidateQueries({
          queryKey: shopStaffKeys.detail(shopId, staffId),
        });
      toast.success("ແກ້ໄຂພະນັກງານສຳເລັດ");
    },
    onError: () => toast.error("ແກ້ໄຂພະນັກງານລົ້ມເຫຼວ"),
  });
}

export function useDeleteShopStaff(shopId: string | undefined) {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: async (input: { staffId: string } | { userId: string }) => {
      if (!shopId) throw new Error("shopId required");
      if ("userId" in input) {
        return shopStaffApi.deleteAllForUser(shopId, input.userId);
      }
      return shopStaffApi.delete(shopId, input.staffId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopStaffKeys.all(shopId) });
    },
  });

  const run = (staffId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(
        { staffId },
        {
          onSuccess: () => resolve(),
          onError: (e) => reject(e),
        },
      );
    });

  const runForUser = (userId: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(
        { userId },
        {
          onSuccess: () => resolve(),
          onError: (e) => reject(e),
        },
      );
    });

  return { ...base, run, runForUser };
}

// ---------------------------------------------------------------------------
// Shop catalog (สินค้าขายได้)
// ---------------------------------------------------------------------------

export const shopCatalogKeys = {
  all: (shopId: string) => ["shops", shopId, "catalog"] as const,
  sellable: (shopId: string, q: SellableCatalogQueryDTO) =>
    [...shopCatalogKeys.all(shopId), "sellable", q] as const,
  sellableSku: (shopId: string, shopProductSkuId: string) =>
    [
      ...shopCatalogKeys.all(shopId),
      "sellable",
      "sku",
      shopProductSkuId,
    ] as const,
  fitmentPendingCount: (shopId: string) =>
    [...shopCatalogKeys.all(shopId), "fitment-pending-count"] as const,
  skuMedia: (shopId: string, shopProductSkuId: string) =>
    [
      ...shopCatalogKeys.all(shopId),
      "sellable",
      "sku",
      shopProductSkuId,
      "media",
    ] as const,
  masterCategories: (shopId: string) =>
    [...shopCatalogKeys.all(shopId), "master-categories"] as const,
  sellableCategories: (shopId: string) =>
    [...shopCatalogKeys.all(shopId), "sellable-categories"] as const,
  match: (
    shopId: string,
    query: { oemPartNo?: string; q?: string; barcode?: string },
  ) => [...shopCatalogKeys.all(shopId), "match", query] as const,
  linkable: (shopId: string, query: { categoryId: string; q?: string }) =>
    [...shopCatalogKeys.all(shopId), "linkable", query] as const,
  suggest: (
    shopId: string,
    query: {
      name: string;
      categoryId?: string;
      type?: string;
    },
  ) => [...shopCatalogKeys.all(shopId), "suggest", query] as const,
};

export function useMasterCategoriesQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopCatalogKeys.masterCategories(shopId ?? ""),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.listMasterCategories(shopId);
    },
    enabled: Boolean(shopId),
  });
}

/** Categories that have sellable products in this shop, each with a product `count`. */
export function useSellableCategoriesQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopCatalogKeys.sellableCategories(shopId ?? ""),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.listSellableCategories(shopId);
    },
    enabled: Boolean(shopId),
  });
}

export function useCatalogMatchQuery(
  shopId: string | undefined,
  query: { oemPartNo?: string; q?: string; barcode?: string },
  enabled: boolean,
) {
  const oem = query.oemPartNo?.trim() ?? "";
  const q = query.q?.trim() ?? "";
  const barcode = query.barcode?.trim() ?? "";
  const hasQuery = oem.length >= 2 || q.length >= 2 || barcode.length >= 2;

  return useQuery({
    queryKey: shopCatalogKeys.match(shopId ?? "", {
      oemPartNo: oem || undefined,
      q: q || undefined,
      barcode: barcode || undefined,
    }),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.matchCatalog(shopId, {
        oemPartNo: oem || undefined,
        q: q || undefined,
        barcode: barcode || undefined,
        limit: 10,
      });
    },
    enabled: enabled && Boolean(shopId) && hasQuery,
    staleTime: 30_000,
  });
}

export function useCatalogSuggestQuery(
  shopId: string | undefined,
  query: {
    name?: string;
    categoryId?: string;
    type?: "part_new" | "part_oem" | "part_aftermarket" | "part_used";
  },
  enabled: boolean,
) {
  const name = query.name?.trim() ?? "";
  const categoryId = query.categoryId?.trim() ?? "";

  return useQuery({
    queryKey: shopCatalogKeys.suggest(shopId ?? "", {
      name,
      categoryId: categoryId || undefined,
      type: query.type,
    }),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.suggestCatalog(shopId, {
        name,
        categoryId: categoryId || undefined,
        type: query.type,
        limit: 5,
      });
    },
    enabled: enabled && Boolean(shopId) && name.length >= 3,
    staleTime: 30_000,
  });
}

export function useLinkableCatalogQuery(
  shopId: string | undefined,
  query: { categoryId: string; q?: string },
  enabled: boolean,
) {
  const q = query.q?.trim() ?? "";

  return useQuery({
    queryKey: shopCatalogKeys.linkable(shopId ?? "", {
      categoryId: query.categoryId,
      q: q || undefined,
    }),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.listLinkableCatalog(shopId, {
        categoryId: query.categoryId,
        q: q || undefined,
        limit: 40,
      });
    },
    enabled: enabled && Boolean(shopId) && Boolean(query.categoryId),
    staleTime: 30_000,
  });
}

function normalizeSellableQuery(
  query: Partial<SellableCatalogQueryDTO>,
): SellableCatalogQueryDTO {
  const limit = query.limit != null ? Number(query.limit) : 20;
  const offset = query.offset != null ? Number(query.offset) : 0;
  return {
    limit: Number.isFinite(limit) && limit > 0 ? limit : 20,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    sort: query.sort,
    filters: query.filters,
    q:
      typeof query.q === "string" && query.q.trim() !== ""
        ? query.q.trim()
        : undefined,
    categoryId:
      typeof query.categoryId === "string" && query.categoryId.trim() !== ""
        ? query.categoryId.trim()
        : undefined,
    fitment:
      query.fitment === "pending" || query.fitment === "complete"
        ? query.fitment
        : undefined,
    currency:
      query.currency === "LAK" ||
      query.currency === "THB" ||
      query.currency === "USD"
        ? query.currency
        : undefined,
    productType:
      query.productType === "part_new" ||
      query.productType === "part_oem" ||
      query.productType === "part_aftermarket" ||
      query.productType === "part_used" ||
      query.productType === "service"
        ? query.productType
        : undefined,
    barcode:
      typeof query.barcode === "string" && query.barcode.trim() !== ""
        ? query.barcode.trim()
        : undefined,
  };
}

export function useShopSellableCatalogQuery(
  shopId: string | undefined,
  query: Partial<SellableCatalogQueryDTO> = {},
) {
  // Catalog management list only: opt in to see this shop's own pending / inactive /
  // rejected rows (badged). normalizeSellableQuery drops unknown fields, so add here.
  const q = {
    ...normalizeSellableQuery(query),
    includePending: true,
    includeInactive: true,
  };
  return useQuery({
    queryKey: shopCatalogKeys.sellable(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.listSellable(shopId, q);
    },
    enabled: !!shopId,
  });
}

export function useShopFitmentPendingCountQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopId
      ? shopCatalogKeys.fitmentPendingCount(shopId)
      : ["shops", "catalog", "fitment-pending-count", "disabled"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.fitmentPendingCount(shopId);
    },
    enabled: !!shopId,
  });
}

const SKU_MEDIA_STALE_MS = 5 * 60_000;
const SKU_MEDIA_MAX_CONCURRENCY = 6;

let mediaFetchesInFlight = 0;
const mediaFetchWaiters: (() => void)[] = [];

/** จำกัด concurrency ของ /media prefetch ไม่ให้ยิงพร้อมกันทั้งหน้า */
async function withMediaFetchSlot<T>(run: () => Promise<T>): Promise<T> {
  if (mediaFetchesInFlight >= SKU_MEDIA_MAX_CONCURRENCY) {
    await new Promise<void>((resolve) => mediaFetchWaiters.push(resolve));
  }
  mediaFetchesInFlight++;
  try {
    return await run();
  } finally {
    mediaFetchesInFlight--;
    mediaFetchWaiters.shift()?.();
  }
}

export function useShopSkuMediaQuery(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  return useQuery({
    queryKey:
      shopId && shopProductSkuId
        ? shopCatalogKeys.skuMedia(shopId, shopProductSkuId)
        : ["shops", "catalog", "sku", "media", "disabled"],
    queryFn: () => {
      if (!shopId || !shopProductSkuId) {
        throw new Error("shopId and shopProductSkuId required");
      }
      return shopCatalogApi.listSkuMedia(shopId, shopProductSkuId);
    },
    enabled: !!shopId && !!shopProductSkuId,
    // ใช้ cache ที่ prefetch ไว้ตอนเปิด modal แต่ยัง refetch พื้นหลังให้ข้อมูลสด
    staleTime: SKU_MEDIA_STALE_MS,
    retry: 1,
  });
}

/** จำนวนรูป + รูปแรกของแต่ละ sellable — ใช้ทำ badge บนตาราง */
export type ShopSkuMediaCount = { count: number; firstUrl: string | null };

/**
 * Prefetch /media ของทุกแถวที่มองเห็น (concurrency ≤ 6) เพื่อเติม badge จำนวนรูป
 * โดยไม่บล็อกการ render ตาราง — ใช้ query key เดียวกับ useShopSkuMediaQuery
 * ดังนั้น lightbox ที่เปิดทีหลังจะได้ข้อมูลจาก cache ทันที
 */
export function useShopSkuMediaCounts(
  shopId: string | undefined,
  shopProductSkuIds: string[],
): Map<string, ShopSkuMediaCount> {
  return useQueries({
    queries: shopProductSkuIds.map((skuId) => ({
      queryKey: shopId
        ? shopCatalogKeys.skuMedia(shopId, skuId)
        : (["shops", "catalog", "sku", "media", "disabled", skuId] as const),
      queryFn: () => {
        if (!shopId) throw new Error("shopId required");
        return withMediaFetchSlot(() =>
          shopCatalogApi.listSkuMediaQuiet(shopId, skuId),
        );
      },
      enabled: !!shopId,
      staleTime: SKU_MEDIA_STALE_MS,
      retry: 1,
      refetchOnWindowFocus: false,
    })),
    // combine ถูก memo ตาม structural sharing ของ results — Map จึงคงตัวตราบที่ข้อมูลไม่เปลี่ยน
    combine: (results) => {
      const map = new Map<string, ShopSkuMediaCount>();
      shopProductSkuIds.forEach((skuId, i) => {
        const media = results[i]?.data;
        if (!media) return;
        map.set(skuId, {
          count: media.length,
          firstUrl: media[0]?.url ?? null,
        });
      });
      return map;
    },
  });
}

function useInvalidateSkuMedia(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  const qc = useQueryClient();
  return () => {
    if (!shopId || !shopProductSkuId) return;
    void qc.invalidateQueries({
      queryKey: shopCatalogKeys.skuMedia(shopId, shopProductSkuId),
    });
    void qc.invalidateQueries({
      queryKey: shopCatalogKeys.sellableSku(shopId, shopProductSkuId),
    });
    void qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
  };
}

export function useAddShopSkuMedia(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  const invalidate = useInvalidateSkuMedia(shopId, shopProductSkuId);
  return useMutation({
    mutationFn: (url: string) => {
      if (!shopId || !shopProductSkuId) {
        throw new Error("shopId and shopProductSkuId required");
      }
      return shopCatalogApi.addSkuMedia(shopId, shopProductSkuId, url);
    },
    onSuccess: () => {
      invalidate();
      toast.success("ເພີ່ມຮູບແລ້ວ");
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : "ເພີ່ມຮູບລົ້ມເຫຼວ";
      if (msg.includes("media_limit_reached") || msg.includes("8 ຮູບ")) {
        toast.error("ເພີ່ມໄດ້ສູງສຸດ 8 ຮູບຕໍ່ SKU");
      } else {
        toast.error(msg);
      }
    },
  });
}

export function useDeleteShopSkuMedia(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  const invalidate = useInvalidateSkuMedia(shopId, shopProductSkuId);
  return useMutation({
    mutationFn: (mediaId: string) => {
      if (!shopId || !shopProductSkuId) {
        throw new Error("shopId and shopProductSkuId required");
      }
      return shopCatalogApi.deleteSkuMedia(shopId, shopProductSkuId, mediaId);
    },
    onSuccess: () => {
      invalidate();
      toast.success("ລຶບຮູບແລ້ວ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ລຶບຮູບລົ້ມເຫຼວ");
    },
  });
}

export function useReorderShopSkuMedia(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  const invalidate = useInvalidateSkuMedia(shopId, shopProductSkuId);
  return useMutation({
    mutationFn: (mediaIds: string[]) => {
      if (!shopId || !shopProductSkuId) {
        throw new Error("shopId and shopProductSkuId required");
      }
      return shopCatalogApi.reorderSkuMedia(shopId, shopProductSkuId, mediaIds);
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ຈັດລຳດັບຮູບລົ້ມເຫຼວ");
    },
  });
}

export function useShopSellableSkuQuery(
  shopId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  return useQuery({
    queryKey:
      shopId && shopProductSkuId
        ? shopCatalogKeys.sellableSku(shopId, shopProductSkuId)
        : ["shops", "catalog", "sellable", "sku", "disabled"],
    queryFn: () => {
      if (!shopId || !shopProductSkuId) {
        throw new Error("shopId and shopProductSkuId required");
      }
      return shopCatalogApi.getSellableSku(shopId, shopProductSkuId);
    },
    enabled: !!shopId && !!shopProductSkuId,
  });
}

/** POS: ຄົ້ນ sellable ຕາມ barcode ຮ້ານ (shop_product_skus.barcode) */
export const shopPosKeys = {
  all: (shopId: string) => ["shopPos", shopId] as const,
  lots: (shopId: string, branchId: string, skuId: string) =>
    [...shopPosKeys.all(shopId), "lots", branchId, skuId] as const,
  serials: (shopId: string, branchId: string, skuId: string) =>
    [...shopPosKeys.all(shopId), "serials", branchId, skuId] as const,
  salesList: (shopId: string, q: Partial<PosSalesListQueryDTO>) =>
    [...shopPosKeys.all(shopId), "sales", q] as const,
  returnSummaries: (
    shopId: string,
    returnType: "return" | "exchange",
    saleIds: string[],
  ) =>
    [
      ...shopPosKeys.all(shopId),
      "returnSummaries",
      returnType,
      saleIds,
    ] as const,
  saleDetail: (shopId: string, saleId: string) =>
    [...shopPosKeys.all(shopId), "saleDetail", saleId] as const,
  saleActivity: (shopId: string, saleId: string) =>
    [...shopPosKeys.all(shopId), "saleActivity", saleId] as const,
  salesByCurrencyReport: (
    shopId: string,
    q: Partial<PosSalesByCurrencyReportQueryDTO>,
  ) => [...shopPosKeys.all(shopId), "salesByCurrencyReport", q] as const,
  salesByDayReport: (
    shopId: string,
    q: Partial<PosSalesExtendedReportQueryDTO>,
  ) => [...shopPosKeys.all(shopId), "salesByDayReport", q] as const,
  topSkusReport: (shopId: string, q: Partial<PosSalesExtendedReportQueryDTO>) =>
    [...shopPosKeys.all(shopId), "topSkusReport", q] as const,
  salesByBranchReport: (
    shopId: string,
    q: Partial<PosSalesExtendedReportQueryDTO>,
  ) => [...shopPosKeys.all(shopId), "salesByBranchReport", q] as const,
  categories: (shopId: string, branchId: string | undefined) =>
    [...shopPosKeys.all(shopId), "categories", branchId ?? ""] as const,
  sellableBrowse: (shopId: string, q: SellableCatalogQueryDTO) =>
    [...shopPosKeys.all(shopId), "sellableBrowse", q] as const,
};

function normalizePosSellableBrowseQuery(
  query: Partial<SellableCatalogQueryDTO>,
): SellableCatalogQueryDTO {
  const base = normalizeSellableQuery(query);
  return {
    ...base,
    branchId:
      typeof query.branchId === "string" && query.branchId.trim() !== ""
        ? query.branchId.trim()
        : undefined,
  };
}

export function usePosCategoriesQuery(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  return useQuery({
    queryKey: shopPosKeys.categories(shopId ?? "", branchId),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.listCategories(shopId, {
        branchId,
        topDays: 30,
      });
    },
    enabled: !!shopId,
    staleTime: 60_000,
  });
}

export function usePosSellableBrowseQuery(
  shopId: string | undefined,
  query: Partial<SellableCatalogQueryDTO> = {},
) {
  const q = normalizePosSellableBrowseQuery(query);
  return useQuery({
    queryKey: shopPosKeys.sellableBrowse(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.listSellable(shopId, q);
    },
    enabled: !!shopId,
  });
}

export const shopReportsKeys = {
  moduleStats: (shopId: string) =>
    ["shopReports", shopId, "moduleStats"] as const,
};

export const shopSettlementKeys = {
  all: (shopId: string) => ["shops", shopId, "settlement"] as const,
  summary: (shopId: string) =>
    [...shopSettlementKeys.all(shopId), "summary"] as const,
  periods: (shopId: string, q: Partial<ShopSettlementPeriodsQueryDTO>) =>
    [...shopSettlementKeys.all(shopId), "periods", q] as const,
  marketplacePeriods: (
    shopId: string,
    q: Partial<ShopSettlementPeriodsQueryDTO>,
  ) => [...shopSettlementKeys.all(shopId), "marketplacePeriods", q] as const,
  payoutRequests: (shopId: string, q: Partial<ShopPayoutRequestsQueryDTO>) =>
    [...shopSettlementKeys.all(shopId), "payoutRequests", q] as const,
};

export function useShopSettlementSummaryQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopId
      ? shopSettlementKeys.summary(shopId)
      : ["shops", "none", "settlement", "summary"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopSettlementApi.summary(shopId);
    },
    enabled: Boolean(shopId),
  });
}

export function useShopSettlementPeriodsQuery(
  shopId: string | undefined,
  query: Partial<ShopSettlementPeriodsQueryDTO>,
) {
  return useQuery({
    queryKey: shopId
      ? shopSettlementKeys.periods(shopId, query)
      : ["shops", "none", "settlement", "periods"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopSettlementApi.listPeriods(shopId, {
        limit: query.limit ?? 20,
        offset: query.offset ?? 0,
        status: query.status,
        createdFrom: query.createdFrom,
        createdTo: query.createdTo,
      });
    },
    enabled: Boolean(shopId),
  });
}

export function useShopMarketplaceSettlementPeriodsQuery(
  shopId: string | undefined,
  query: Partial<ShopSettlementPeriodsQueryDTO>,
) {
  return useQuery({
    queryKey: shopId
      ? shopSettlementKeys.marketplacePeriods(shopId, query)
      : ["shops", "none", "settlement", "marketplacePeriods"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopSettlementApi.listMarketplacePeriods(shopId, {
        limit: query.limit ?? 20,
        offset: query.offset ?? 0,
        status: query.status,
        createdFrom: query.createdFrom,
        createdTo: query.createdTo,
      });
    },
    enabled: Boolean(shopId),
  });
}

export function useShopPayoutRequestsQuery(
  shopId: string | undefined,
  query: Partial<ShopPayoutRequestsQueryDTO>,
) {
  return useQuery({
    queryKey: shopId
      ? shopSettlementKeys.payoutRequests(shopId, query)
      : ["shops", "none", "settlement", "payoutRequests"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopSettlementApi.listPayoutRequests(shopId, {
        limit: query.limit ?? 20,
        offset: query.offset ?? 0,
        status: query.status,
        currency: query.currency,
        createdFrom: query.createdFrom,
        createdTo: query.createdTo,
      });
    },
    enabled: Boolean(shopId),
  });
}

export function useCreateShopPayoutRequestMutation(shopId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShopPayoutRequestDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopSettlementApi.createPayoutRequest(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        void queryClient.invalidateQueries({
          queryKey: shopSettlementKeys.all(shopId),
        });
      }
    },
  });
}

export function usePosSalesByCurrencyReportQuery(
  shopId: string | undefined,
  query: Partial<PosSalesByCurrencyReportQueryDTO>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.salesByCurrencyReport(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.salesByCurrencyReport(shopId, query);
    },
    enabled: Boolean(shopId) && enabled,
  });
}

export function usePosSalesByDayReportQuery(
  shopId: string | undefined,
  query: Partial<PosSalesExtendedReportQueryDTO>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.salesByDayReport(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.salesByDayReport(shopId, query);
    },
    enabled: Boolean(shopId) && enabled,
  });
}

export function usePosTopSkusReportQuery(
  shopId: string | undefined,
  query: Partial<PosSalesExtendedReportQueryDTO>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.topSkusReport(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.topSkusReport(shopId, query);
    },
    enabled: Boolean(shopId) && enabled,
  });
}

export function usePosSalesByBranchReportQuery(
  shopId: string | undefined,
  query: Partial<PosSalesExtendedReportQueryDTO>,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.salesByBranchReport(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.salesByBranchReport(shopId, query);
    },
    enabled: Boolean(shopId) && enabled,
  });
}

export function useShopModuleReportStatsQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopId
      ? shopReportsKeys.moduleStats(shopId)
      : ["shopReports", "none", "moduleStats"],
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopReportsApi.moduleStats(shopId);
    },
    enabled: Boolean(shopId),
  });
}

export function usePosLotsForSkuQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  shopProductSkuId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.lots(
      shopId ?? "",
      branchId ?? "",
      shopProductSkuId ?? "",
    ),
    queryFn: () => {
      if (!shopId || !branchId || !shopProductSkuId) {
        throw new Error("shopId, branchId, shopProductSkuId required");
      }
      return shopPosApi.listLots(shopId, branchId, shopProductSkuId);
    },
    enabled: Boolean(shopId && branchId && shopProductSkuId) && enabled,
  });
}

export function usePosSerialsForSkuQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  shopProductSkuId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.serials(
      shopId ?? "",
      branchId ?? "",
      shopProductSkuId ?? "",
    ),
    queryFn: () => {
      if (!shopId || !branchId || !shopProductSkuId) {
        throw new Error("shopId, branchId, shopProductSkuId required");
      }
      return shopPosApi.listSerials(shopId, branchId, shopProductSkuId);
    },
    enabled: Boolean(shopId && branchId && shopProductSkuId) && enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function usePosSerialLookupMutation(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  return useMutation({
    mutationFn: async (code: string) => {
      if (!shopId || !branchId) {
        throw new Error("shopId and branchId required");
      }
      const trimmed = code.trim();
      if (!trimmed) throw new Error("code required");
      return shopPosApi.lookupSerial(shopId, branchId, trimmed);
    },
  });
}

export function usePosScanResolveMutation(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  return useMutation({
    mutationFn: async (code: string) => {
      if (!shopId || !branchId) {
        throw new Error("shopId and branchId required");
      }
      const trimmed = code.trim();
      if (!trimmed) throw new Error("code required");
      return shopPosApi.scanResolve(shopId, branchId, trimmed);
    },
  });
}

export function usePosCheckoutMutation(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      body: PosCheckoutDTO;
      idempotencyKey: string;
    }) => {
      if (!shopId || !branchId) {
        throw new Error("shopId and branchId required");
      }
      return shopPosApi.checkout(
        shopId,
        branchId,
        args.body,
        args.idempotencyKey,
      );
    },
    onSuccess: (_data, _vars, _ctx) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
    },
  });
}

/** ຕົວຢ່າງຍອດ (ສ່ວນຫຼຸດໂປຣ + ມື + VAT) ໃຫ້ຕົງກັບ POST /pos/checkout */
export function usePosCheckoutPreviewQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  body: PosCheckoutPreviewDTO | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [
      ...shopPosKeys.all(shopId ?? ""),
      "checkoutPreview",
      branchId ?? "",
      body,
    ] as const,
    queryFn: () =>
      shopPosApi.checkoutPreview(
        shopId!,
        branchId!,
        body as PosCheckoutPreviewDTO,
      ),
    enabled:
      Boolean(shopId && branchId && body) &&
      (options?.enabled ?? true) !== false,
    staleTime: 0,
  });
}

export function usePosAddSalePaymentsMutation(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      saleId: string;
      body: PosSaleAddPaymentsDTO;
    }) => {
      if (!shopId || !branchId) {
        throw new Error("shopId and branchId required");
      }
      return shopPosApi.addSalePayments(
        shopId,
        branchId,
        args.saleId,
        args.body,
      );
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
    },
  });
}

export function usePosSalesListQuery(
  shopId: string | undefined,
  query: Partial<PosSalesListQueryDTO>,
) {
  return useQuery({
    queryKey: shopPosKeys.salesList(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.listSales(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function usePosReturnSummariesQuery(
  shopId: string | undefined,
  returnType: "return" | "exchange" | undefined,
  saleIds: string[],
) {
  return useQuery({
    queryKey: shopPosKeys.returnSummaries(
      shopId ?? "",
      returnType ?? "return",
      saleIds,
    ),
    queryFn: () => {
      if (!shopId || !returnType) throw new Error("shopId/returnType required");
      return shopPosApi.returnSummaries(shopId, returnType, saleIds);
    },
    enabled: !!shopId && !!returnType && saleIds.length > 0,
  });
}

export function usePosSaleDetailQuery(
  shopId: string | undefined,
  saleId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: shopPosKeys.saleDetail(shopId ?? "", saleId ?? ""),
    queryFn: () => {
      if (!shopId || !saleId) throw new Error("shopId and saleId required");
      return shopPosApi.getSale(shopId, saleId);
    },
    enabled: !!shopId && !!saleId && enabled,
  });
}

export function usePosSaleActivityQuery(
  shopId: string | undefined,
  saleId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: shopPosKeys.saleActivity(shopId ?? "", saleId ?? ""),
    queryFn: () => {
      if (!shopId || !saleId) throw new Error("shopId and saleId required");
      return shopPosApi.getSaleActivity(shopId, saleId);
    },
    enabled: !!shopId && !!saleId && enabled,
  });
}

/** ຄືນ/ປ່ຽນສິນຄ້າ POS — invalidate ບິນ + ສະຕ໋ອກ */
export function useCreatePosReturnMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      branchId: string;
      body: import("./client").CreatePosReturnBody;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.createReturn(shopId, vars.branchId, vars.body);
    },
    onSuccess: () => {
      if (!shopId) return;
      void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      // ສະຕ໋ອກ/inventory ປ່ຽນຫຼັງ restock
      void qc.invalidateQueries({ queryKey: ["shops", shopId, "inventory"] });
    },
  });
}

export function usePosCreateHoldMutation(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PosCreateHoldDTO) => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopPosApi.createHold(shopId, branchId, body);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
    },
  });
}

export function usePosConfirmHoldMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      saleId: string;
      branchId: string;
      body: PosHoldConfirmDTO;
      idempotencyKey: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.confirmHold(
        shopId,
        args.branchId,
        args.saleId,
        args.body,
        args.idempotencyKey,
      );
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
      }
    },
  });
}

export function usePosVoidSaleMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { saleId: string; branchId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.voidSale(shopId, args.saleId, args.branchId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
    },
  });
}

export function usePosRefundSaleMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { saleId: string; branchId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.refundSale(shopId, args.saleId, args.branchId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
    },
  });
}

export function usePosAbandonHoldMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { saleId: string; branchId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPosApi.abandonHold(shopId, args.branchId, args.saleId);
    },
    onSuccess: () => {
      if (shopId) qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
    },
  });
}

export function useLookupSellableByBarcodeMutation(shopId: string | undefined) {
  return useMutation({
    mutationFn: async (rawBarcode: string) => {
      if (!shopId) throw new Error("shopId required");
      const trimmed = rawBarcode.trim();
      if (!trimmed) throw new Error("barcode required");
      return shopCatalogApi.listSellable(shopId, {
        limit: 10,
        offset: 0,
        q: undefined,
        barcode: trimmed,
      });
    },
  });
}

export function usePatchSellableShopProductSku(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      shopProductSkuId: string;
      body: UpdateShopProductSkuDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.patchSellableSku(
        shopId,
        args.shopProductSkuId,
        args.body,
      );
    },
    onSuccess: (_data, variables) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopCatalogKeys.sellableSku(
            shopId,
            variables.shopProductSkuId,
          ),
        });
      }
      toast.success("ບັນທຶກສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກລົ້ມເຫຼວ");
    },
  });
}

// ---------------------------------------------------------------------------
// Shop inventory (Phase E)
// ---------------------------------------------------------------------------

export const shopInventoryKeys = {
  all: (shopId: string) => ["shops", shopId, "inventory"] as const,
  summary: (shopId: string) =>
    [...shopInventoryKeys.all(shopId), "summary"] as const,
  stock: (shopId: string, q: ShopInventoryListQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "stock", q] as const,
  movements: (shopId: string, q: ShopInventoryMovementsQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "movements", q] as const,
  reconcile: (shopId: string, q: ShopInventoryReconcileQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "reconcile", q] as const,
  lotLabels: (shopId: string, q: ShopInventoryLotLabelsQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "lotLabels", q] as const,
  serials: (shopId: string, q: ShopInventorySerialsQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "inventorySerials", q] as const,
  zones: (shopId: string, branchId: string) =>
    [...shopInventoryKeys.all(shopId), "warehouseZones", branchId] as const,
  layout: (shopId: string, branchId: string, layoutId?: string) =>
    [
      ...shopInventoryKeys.all(shopId),
      "warehouseLayout",
      branchId,
      layoutId ?? "default",
    ] as const,
  layoutFloors: (shopId: string, branchId: string) =>
    [
      ...shopInventoryKeys.all(shopId),
      "warehouseLayoutFloors",
      branchId,
    ] as const,
  layoutPlacements: (shopId: string, branchId: string) =>
    [
      ...shopInventoryKeys.all(shopId),
      "warehouseLayoutPlacements",
      branchId,
    ] as const,
  locations: (shopId: string, branchId: string, zoneId?: string) =>
    [
      ...shopInventoryKeys.all(shopId),
      "warehouseLocations",
      branchId,
      zoneId ?? "all",
    ] as const,
  skuLocations: (shopId: string, branchId: string, shopProductSkuId: string) =>
    [
      ...shopInventoryKeys.all(shopId),
      "skuLocations",
      branchId,
      shopProductSkuId,
    ] as const,
  byLocation: (shopId: string, q: InventoryByLocationQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "byLocation", q] as const,
  unlocatedReport: (shopId: string, q: WarehouseUnlocatedReportQueryDTO) =>
    [...shopInventoryKeys.all(shopId), "unlocatedReport", q] as const,
  emptyLocationsReport: (
    shopId: string,
    q: WarehouseEmptyLocationsReportQueryDTO,
  ) => [...shopInventoryKeys.all(shopId), "emptyLocationsReport", q] as const,
  receiveByLocationReport: (
    shopId: string,
    q: WarehouseReceiveByLocationReportQueryDTO,
  ) =>
    [...shopInventoryKeys.all(shopId), "receiveByLocationReport", q] as const,
};

function normalizeShopInventoryMovementsQuery(
  query: Partial<ShopInventoryMovementsQueryDTO>,
): ShopInventoryMovementsQueryDTO {
  const limit = query.limit != null ? Number(query.limit) : 20;
  const offset = query.offset != null ? Number(query.offset) : 0;
  const typeRaw = query.type;
  const allowed = InventoryMovementTypeSchema.options as readonly string[];
  const type =
    typeRaw && allowed.includes(typeRaw)
      ? (typeRaw as InventoryMovementTypeDTO)
      : undefined;
  return {
    limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : 20,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    sort: query.sort,
    filters: query.filters,
    branchId:
      typeof query.branchId === "string" && query.branchId.trim() !== ""
        ? query.branchId.trim()
        : undefined,
    shopProductSkuId:
      typeof query.shopProductSkuId === "string" &&
      query.shopProductSkuId.trim() !== ""
        ? query.shopProductSkuId.trim()
        : undefined,
    type,
    createdFrom:
      typeof query.createdFrom === "string" && query.createdFrom.trim() !== ""
        ? query.createdFrom.trim()
        : undefined,
    createdTo:
      typeof query.createdTo === "string" && query.createdTo.trim() !== ""
        ? query.createdTo.trim()
        : undefined,
  };
}

function normalizeShopInventoryReconcileQuery(
  query: Partial<ShopInventoryReconcileQueryDTO>,
): ShopInventoryReconcileQueryDTO {
  const limit = query.limit != null ? Number(query.limit) : 100;
  return {
    branchId:
      typeof query.branchId === "string" && query.branchId.trim() !== ""
        ? query.branchId.trim()
        : undefined,
    shopProductSkuId:
      typeof query.shopProductSkuId === "string" &&
      query.shopProductSkuId.trim() !== ""
        ? query.shopProductSkuId.trim()
        : undefined,
    onlyMismatch: query.onlyMismatch !== false,
    limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 500) : 100,
  };
}

function normalizeShopInventoryListQuery(
  query: Partial<ShopInventoryListQueryDTO>,
): ShopInventoryListQueryDTO {
  const limit = query.limit != null ? Number(query.limit) : 20;
  const offset = query.offset != null ? Number(query.offset) : 0;
  return {
    limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : 20,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    sort: query.sort,
    filters: query.filters,
    q:
      typeof query.q === "string" && query.q.trim() !== ""
        ? query.q.trim()
        : undefined,
    categoryId:
      typeof query.categoryId === "string" && query.categoryId.trim() !== ""
        ? query.categoryId.trim()
        : undefined,
    branchId:
      typeof query.branchId === "string" && query.branchId.trim() !== ""
        ? query.branchId.trim()
        : undefined,
    shopProductSkuId:
      typeof query.shopProductSkuId === "string" &&
      query.shopProductSkuId.trim() !== ""
        ? query.shopProductSkuId.trim()
        : undefined,
    locationId:
      typeof query.locationId === "string" && query.locationId.trim() !== ""
        ? query.locationId.trim()
        : undefined,
  };
}

export function useShopInventorySummaryQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopInventoryKeys.summary(shopId ?? ""),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.getSummary(shopId);
    },
    enabled: !!shopId,
  });
}

export function useShopInventoryMovementsQuery(
  shopId: string | undefined,
  query: Partial<ShopInventoryMovementsQueryDTO> = {},
  options?: { enabled?: boolean },
) {
  const q = normalizeShopInventoryMovementsQuery(query);
  return useQuery({
    queryKey: shopInventoryKeys.movements(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.listMovements(shopId, q);
    },
    enabled: !!shopId && (options?.enabled ?? true),
  });
}

function normalizeShopInventoryLotLabelsQuery(
  branchId: string,
  shopProductSkuId: string,
  partial: { limit?: number; offset?: number },
): ShopInventoryLotLabelsQueryDTO {
  const limit = partial.limit != null ? Number(partial.limit) : 20;
  const offset = partial.offset != null ? Number(partial.offset) : 0;
  return {
    branchId,
    shopProductSkuId,
    limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : 20,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
  };
}

function normalizeShopInventorySerialsQueryInput(
  branchId: string,
  shopProductSkuId: string,
  partial: { limit?: number; offset?: number; lotId?: string },
): ShopInventorySerialsQueryDTO {
  const limit = partial.limit != null ? Number(partial.limit) : 20;
  const offset = partial.offset != null ? Number(partial.offset) : 0;
  const lotId =
    typeof partial.lotId === "string" && partial.lotId.trim() !== ""
      ? partial.lotId.trim()
      : undefined;
  return {
    branchId,
    shopProductSkuId,
    limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : 20,
    offset: Number.isFinite(offset) && offset >= 0 ? offset : 0,
    lotId,
  };
}

export function useShopInventoryReconcileQuery(
  shopId: string | undefined,
  query: Partial<ShopInventoryReconcileQueryDTO> = {},
) {
  const q = normalizeShopInventoryReconcileQuery(query);
  return useQuery({
    queryKey: shopInventoryKeys.reconcile(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.getReconcile(shopId, q);
    },
    enabled: !!shopId,
  });
}

export function useShopInventoryStockQuery(
  shopId: string | undefined,
  query: Partial<ShopInventoryListQueryDTO> = {},
) {
  const q = normalizeShopInventoryListQuery(query);
  return useQuery({
    queryKey: shopInventoryKeys.stock(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.listStock(shopId, q);
    },
    enabled: !!shopId,
  });
}

/** ຍອດຄົງເຫຼືອຕາມ (ສາຂາ + SKU) — ຟອມຮັບເຂົ້າ / ປັບສະຕ໋ອກ */
export function useShopInventoryBranchSkuStock(
  shopId: string | undefined,
  args: {
    branchId: string | undefined;
    shopProductSkuId: string | undefined;
    enabled?: boolean;
  },
) {
  const q = normalizeShopInventoryListQuery({
    branchId: args.branchId,
    shopProductSkuId: args.shopProductSkuId,
    limit: 1,
    offset: 0,
  });
  return useQuery({
    queryKey: shopInventoryKeys.stock(shopId ?? "", q),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.listStock(shopId, q);
    },
    enabled:
      (args.enabled ?? true) &&
      !!shopId &&
      !!args.branchId &&
      !!args.shopProductSkuId,
  });
}

export function useShopInventoryLotLabelsQuery(
  shopId: string | undefined,
  args: {
    branchId: string | undefined;
    shopProductSkuId: string | undefined;
    limit?: number;
    offset?: number;
    enabled?: boolean;
  },
) {
  const q =
    args.branchId && args.shopProductSkuId
      ? normalizeShopInventoryLotLabelsQuery(
          args.branchId,
          args.shopProductSkuId,
          { limit: args.limit, offset: args.offset },
        )
      : null;
  return useQuery({
    queryKey: q
      ? shopInventoryKeys.lotLabels(shopId ?? "", q)
      : [...shopInventoryKeys.all(shopId ?? ""), "lotLabels", "idle"],
    queryFn: () => {
      if (!shopId || !q) {
        throw new Error("shopId, branchId, shopProductSkuId required");
      }
      return shopInventoryApi.listLotLabels(shopId, q);
    },
    enabled: (args.enabled ?? true) && !!shopId && !!q,
  });
}

export function useShopInventorySerialsQuery(
  shopId: string | undefined,
  args: {
    branchId: string | undefined;
    shopProductSkuId: string | undefined;
    limit?: number;
    offset?: number;
    lotId?: string;
    enabled?: boolean;
  },
) {
  const q =
    args.branchId && args.shopProductSkuId
      ? normalizeShopInventorySerialsQueryInput(
          args.branchId,
          args.shopProductSkuId,
          {
            limit: args.limit,
            offset: args.offset,
            lotId: args.lotId,
          },
        )
      : null;
  return useQuery({
    queryKey: q
      ? shopInventoryKeys.serials(shopId ?? "", q)
      : [...shopInventoryKeys.all(shopId ?? ""), "inventorySerials", "idle"],
    queryFn: () => {
      if (!shopId || !q) {
        throw new Error("shopId, branchId, shopProductSkuId required");
      }
      return shopInventoryApi.listInventorySerials(shopId, q);
    },
    enabled: (args.enabled ?? true) && !!shopId && !!q,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

export function useAdjustShopInventoryMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ShopInventoryAdjustDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.adjust(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
      }
      toast.success("ປັບສະຕ໋ອກສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ປັບສະຕ໋ອກລົ້ມເຫຼວ");
    },
  });
}

export function useReceiveShopGoodsMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ShopInventoryGoodsReceiptDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.receiveGoods(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
      }
      toast.success("ບັນທຶກຮັບເຂົ້າສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ຮັບເຂົ້າລົ້ມເຫຼວ");
    },
  });
}

export function useWarehouseZonesQuery(
  shopId: string | undefined,
  branchId: string | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.zones(shopId ?? "", branchId ?? ""),
    queryFn: () => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopInventoryApi.listWarehouseZones(shopId, branchId);
    },
    enabled: !!shopId && !!branchId,
  });
}

export function useWarehouseLayoutFloorsQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: shopInventoryKeys.layoutFloors(shopId ?? "", branchId ?? ""),
    queryFn: () => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopInventoryApi.listWarehouseLayoutFloors(shopId, branchId);
    },
    enabled: !!shopId && !!branchId && enabled,
  });
}

export function useWarehouseLayoutPlacementsQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: shopInventoryKeys.layoutPlacements(shopId ?? "", branchId ?? ""),
    queryFn: () => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopInventoryApi.listWarehouseLayoutPlacements(shopId, branchId);
    },
    enabled: !!shopId && !!branchId && enabled,
  });
}

export function useAssignWarehouseZoneFloorMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.assignWarehouseZoneFloor>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.assignWarehouseZoneFloor(shopId, body);
    },
    onSuccess: (data, vars) => {
      if (shopId) {
        qc.setQueryData(
          shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
          data.placements,
        );
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
      }
      toast.success(data.layoutId ? "ມອບໂຊນກັບຊັ້ນແລ້ວ" : "ຖອດໂຊນອອກຈາກຜັງແລ້ວ");
    },
    onError: () => toast.error("ມອບໂຊນກັບຊັ້ນລົ້ມເຫຼວ"),
  });
}

export function useWarehouseLayoutQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  layoutId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: shopInventoryKeys.layout(shopId ?? "", branchId ?? "", layoutId),
    queryFn: () => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopInventoryApi.getWarehouseLayout(shopId, branchId, {
        layoutId,
      });
    },
    enabled: !!shopId && !!branchId && enabled,
  });
}

export function useCreateWarehouseLayoutFloorMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.createWarehouseLayoutFloor>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.createWarehouseLayoutFloor(shopId, body);
    },
    onSuccess: (data) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, data.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, data.branchId),
        });
      }
      toast.success("ເພີ່ມຊັ້ນສຳເລັດ");
    },
    onError: () => toast.error("ເພີ່ມຊັ້ນລົ້ມເຫຼວ"),
  });
}

export function useRenameWarehouseLayoutFloorMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      branchId: string;
      layoutId: string;
      name: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.upsertWarehouseLayout(shopId, {
        branchId: body.branchId,
        layoutId: body.layoutId,
        name: body.name,
      });
    },
    onSuccess: (_data, vars) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layout(
            shopId,
            vars.branchId,
            vars.layoutId,
          ),
        });
      }
      toast.success("ປ່ຽນຊື່ຊັ້ນສຳເລັດ");
    },
    onError: () => toast.error("ປ່ຽນຊື່ຊັ້ນລົ້ມເຫຼວ"),
  });
}

export function useDeleteWarehouseLayoutMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { branchId: string; layoutId?: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.deleteWarehouseLayout(
        shopId,
        args.branchId,
        args.layoutId,
      );
    },
    onSuccess: (_data, vars) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
      }
      toast.success("ລຶບຊັ້ນສຳເລັດ");
    },
    onError: () => toast.error("ລຶບຊັ້ນລົ້ມເຫຼວ"),
  });
}

export function useReplaceWarehouseLayoutNodesMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.replaceWarehouseLayoutNodes>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.replaceWarehouseLayoutNodes(shopId, body);
    },
    onSuccess: (data) => {
      if (shopId) {
        const layoutId = data.layout.id ?? undefined;
        qc.setQueryData(
          shopInventoryKeys.layout(shopId, data.layout.branchId, layoutId),
          data,
        );
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(
            shopId,
            data.layout.branchId,
          ),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(
            shopId,
            data.layout.branchId,
          ),
        });
      }
      toast.success("ບັນທຶກແຜນຜັງສຳເລັດ");
    },
    onError: () => toast.error("ບັນທຶກແຜນຜັງລົ້ມເຫຼວ"),
  });
}

export function useGenerateWarehouseLayoutMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.generateWarehouseLayout>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.generateWarehouseLayout(shopId, body);
    },
    onSuccess: (data) => {
      if (shopId) {
        const layoutId = data.layout.id ?? undefined;
        qc.setQueryData(
          shopInventoryKeys.layout(shopId, data.layout.branchId, layoutId),
          data,
        );
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(
            shopId,
            data.layout.branchId,
          ),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(
            shopId,
            data.layout.branchId,
          ),
        });
      }
      toast.success("ສ້າງຜັງຈາກລາຍການສຳເລັດ");
    },
    onError: () => toast.error("ສ້າງຜັງລົ້ມເຫຼວ"),
  });
}

export function useSetWarehouseLayoutBackgroundMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.setWarehouseLayoutBackground>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.setWarehouseLayoutBackground(shopId, body);
    },
    onSuccess: (data) => {
      if (shopId) {
        const layoutId = data.layout.id ?? undefined;
        qc.setQueryData(
          shopInventoryKeys.layout(shopId, data.layout.branchId, layoutId),
          data,
        );
      }
      toast.success("ອັບເດດພື້ນຫຼັງສຳເລັດ");
    },
    onError: () => toast.error("ອັບເດດພື້ນຫຼັງລົ້ມເຫຼວ"),
  });
}

export function useWarehouseLocationsQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  zoneId?: string,
) {
  return useQuery({
    queryKey: shopInventoryKeys.locations(shopId ?? "", branchId ?? "", zoneId),
    queryFn: () => {
      if (!shopId || !branchId) throw new Error("shopId and branchId required");
      return shopInventoryApi.listWarehouseLocations(shopId, {
        branchId,
        zoneId,
      });
    },
    enabled: !!shopId && !!branchId,
  });
}

export function useCreateWarehouseZoneMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.createWarehouseZone>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.createWarehouseZone(shopId, body);
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        qc.invalidateQueries({
          queryKey: shopInventoryKeys.zones(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
      }
      toast.success("ເພີ່ມໂຊນສຳເລັດ");
    },
    onError: () => toast.error("ເພີ່ມໂຊນລົ້ມເຫຼວ"),
  });
}

export function useUpdateWarehouseZoneMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      zoneId: string;
      branchId: string;
      body: Parameters<typeof shopInventoryApi.updateWarehouseZone>[2];
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.updateWarehouseZone(
        shopId,
        vars.zoneId,
        vars.body,
      );
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.zones(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
      }
      toast.success("ແກ້ໄຂໂຊນສຳເລັດ");
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "ແກ້ໄຂໂຊນລົ້ມເຫຼວ"),
  });
}

export function useCreateWarehouseLocationMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: Parameters<typeof shopInventoryApi.createWarehouseLocation>[1],
    ) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.createWarehouseLocation(shopId, body);
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLocations",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutFloors(shopId, vars.branchId),
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
      }
      toast.success("ເພີ່ມຕຳແໜ່ງສຳເລັດ");
    },
    onError: () => toast.error("ເພີ່ມຕຳແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useDeleteWarehouseZoneMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (zoneId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.deleteWarehouseZone(shopId, zoneId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
      }
      toast.success("ລຶບໂຊນສຳເລັດ");
    },
  });
}

export function useDeleteWarehouseLocationMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (locationId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.deleteWarehouseLocation(shopId, locationId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
      }
      toast.success("ລຶກຕຳແໜ່ງສຳເລັດ");
    },
  });
}

export function useSkuLocationBalancesQuery(
  shopId: string | undefined,
  branchId: string | undefined,
  shopProductSkuId: string | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.skuLocations(
      shopId ?? "",
      branchId ?? "",
      shopProductSkuId ?? "",
    ),
    queryFn: () => {
      if (!shopId || !branchId || !shopProductSkuId) {
        throw new Error("shopId, branchId, shopProductSkuId required");
      }
      return shopInventoryApi.listSkuLocationBalances(
        shopId,
        shopProductSkuId,
        branchId,
      );
    },
    enabled: !!shopId && !!branchId && !!shopProductSkuId,
  });
}

export function useInventoryByLocationQuery(
  shopId: string | undefined,
  query: InventoryByLocationQueryDTO | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.byLocation(
      shopId ?? "",
      query ?? {
        branchId: "",
        locationId: "",
        limit: 20,
        offset: 0,
      },
    ),
    queryFn: () => {
      if (!shopId || !query?.branchId || !query.locationId) {
        throw new Error("shopId, branchId, locationId required");
      }
      return shopInventoryApi.listInventoryByLocation(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId && !!query?.locationId,
  });
}

export function useUnlocatedInventoryReportQuery(
  shopId: string | undefined,
  query: WarehouseUnlocatedReportQueryDTO | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.unlocatedReport(
      shopId ?? "",
      query ?? {
        branchId: "",
        limit: 20,
        offset: 0,
      },
    ),
    queryFn: () => {
      if (!shopId || !query?.branchId) {
        throw new Error("shopId, branchId required");
      }
      return shopInventoryApi.listUnlocatedInventoryReport(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId,
  });
}

export function useEmptyWarehouseLocationsReportQuery(
  shopId: string | undefined,
  query: WarehouseEmptyLocationsReportQueryDTO | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.emptyLocationsReport(
      shopId ?? "",
      query ?? {
        branchId: "",
        limit: 20,
        offset: 0,
      },
    ),
    queryFn: () => {
      if (!shopId || !query?.branchId) {
        throw new Error("shopId, branchId required");
      }
      return shopInventoryApi.listEmptyWarehouseLocationsReport(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId,
  });
}

export function useReceiveByLocationReportQuery(
  shopId: string | undefined,
  query: WarehouseReceiveByLocationReportQueryDTO | undefined,
) {
  return useQuery({
    queryKey: shopInventoryKeys.receiveByLocationReport(
      shopId ?? "",
      query ?? { limit: 20, offset: 0 },
    ),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.listReceiveByLocationReport(shopId, query!);
    },
    enabled: !!shopId && !!query,
  });
}

export function useAssignInventoryLocationMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: InventoryAssignLocationDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.assignInventoryLocation(shopId, body);
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopInventoryKeys.skuLocations(
            shopId,
            vars.branchId,
            vars.shopProductSkuId,
          ),
        });
      }
      toast.success("ຍ້າຍຕຳແໜ່ງສຳເລັດ");
    },
    onError: () => toast.error("ຍ້າຍຕຳແໜ່ງລົ້ມເຫຼວ"),
  });
}

export function useLookupWarehouseLocationMutation(shopId: string | undefined) {
  return useMutation({
    mutationFn: (scanCode: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.lookupWarehouseLocationByScan(shopId, scanCode);
    },
  });
}

export function useUpdateWarehouseLocationMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      locationId: string;
      body: Parameters<typeof shopInventoryApi.updateWarehouseLocation>[2];
      branchId: string;
      zoneId?: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.updateWarehouseLocation(
        shopId,
        args.locationId,
        args.body,
      );
    },
    onSuccess: (_d, vars) => {
      if (shopId) {
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLocations",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: [
            ...shopInventoryKeys.all(shopId),
            "warehouseLayout",
            vars.branchId,
          ],
        });
        void qc.invalidateQueries({
          queryKey: shopInventoryKeys.layoutPlacements(shopId, vars.branchId),
        });
      }
      toast.success("ອັບເດດຕຳແໜ່ງສຳເລັດ");
    },
    onError: (err: unknown) => {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message?: string }).message)
          : "";
      toast.error(
        msg.includes("ຊ້ຳ") || msg.includes("DUPLICATE")
          ? "ລະຫັດຕຳແໜ່ງຊ້ຳ"
          : "ອັບເດດຕຳແໜ່ງລົ້ມເຫຼວ",
      );
    },
  });
}

export function usePatchInventoryLotScanCodeMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      lotId: string;
      body: { lotScanCode?: string | null; regenerate?: boolean };
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.patchLotScanCode(shopId, args.lotId, args.body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
      toast.success("ບັນທຶກລະຫັດສະແກນ lot ສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກລະຫັດສະແກນ lot ລົ້ມເຫຼວ");
    },
  });
}

export function useCreateStockTransferMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ShopStockTransferDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.createStockTransfer(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
      }
      toast.success("ໂອນສະຕ໋ອກສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ໂອນລົ້ມເຫຼວ");
    },
  });
}

export function useReserveShopInventoryMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ShopInventoryReserveDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.reserveStock(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
      }
      toast.success("ຈອງສະຕ໋ອກສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ຈອງລົ້ມເຫຼວ");
    },
  });
}

export function useReleaseShopInventoryMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ShopInventoryReleaseDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopInventoryApi.releaseStock(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
      }
      toast.success("ປົດຈອງສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ປົດຈອງລົ້ມເຫຼວ");
    },
  });
}

/** ເປີດ/ປິດ SKU ຂາຍໄດ້ (isActive) — invalidate ລາຍການ sellable + POS */
export function useSetSellableActiveMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { shopProductSkuId: string; isActive: boolean }) => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.setSellableActive(
        shopId,
        vars.shopProductSkuId,
        vars.isActive,
      );
    },
    onSuccess: (res) => {
      if (shopId) {
        void qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
      toast.success(res.isActive ? "ເປີດໃຊ້ງານສິນຄ້າແລ້ວ" : "ປິດສິນຄ້າແລ້ວ");
    },
    onError: () => {
      toast.error("ອັບເດດສະຖານະສິນຄ້າລົ້ມເຫຼວ");
    },
  });
}

/** ລຶບ SKU ອອກຈາກຮ້ານ — ສະແດງເຫດຜົນຈາກ server ຖ້າລຶບບໍ່ໄດ້ (409 in_use) */
export function useDeleteSellableMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shopProductSkuId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.deleteSellable(shopId, shopProductSkuId);
    },
    onSuccess: () => {
      if (shopId) {
        void qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
      toast.success("ລຶບສິນຄ້າອອກຈາກຮ້ານແລ້ວ");
    },
    onError: (err) =>
      toast.error(
        err instanceof Error && err.message ? err.message : "ລຶບສິນຄ້າບໍ່ໄດ້",
      ),
  });
}

export function useProvisionSellableProduct(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ProvisionPosSellableProductDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopCatalogApi.provisionSellableProduct(shopId, body);
    },
    onSuccess: (data) => {
      if (shopId) {
        void qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        void qc.invalidateQueries({ queryKey: shopPosKeys.all(shopId) });
      }
      if (data.linked) {
        toast.success("ຜູກກັບ catalog ແລ້ວ — ຂາຍໄດ້ທັນທີ");
      } else if (data.candidate) {
        toast.success("ສົ່ງ candidate ແລ້ວ — ຂາຍໄດ້ຊົ່ວຄາວ · ລໍ Admin ອະນຸມັດ master");
      } else {
        toast.success(
          "ເພີ່ມສິນຄ້າແລ້ວ — ຂາຍໄດ້ທັນທີ · Admin ກວດ/ຄີ fitment ຕາມຄວາມຈຳເປັນ",
        );
      }
    },
    onError: (e) => {
      const msg =
        e instanceof Error
          ? e.message
          : typeof e === "object" && e && "message" in e
            ? String((e as { message: string }).message)
            : "ເພີ່ມສິນຄ້າລົ້ມເຫຼວ";
      if (
        msg.includes("BARCODE_IN_USE") ||
        msg.includes("409") ||
        msg.toLowerCase().includes("barcode")
      )
        toast.error("Barcode ນີ້ໃຊ້ໃນຮ້ານແລ້ວ");
      else if (
        msg.includes("SHOP_SKU_IN_USE") ||
        msg.toLowerCase().includes("sku")
      )
        toast.error("Shop SKU ນີ້ມີແລ້ວ");
      else if (msg.includes("ALREADY_LINKED"))
        toast.error("ຮ້ານນີ້ມີລາຍການ catalog ນີ້ແລ້ວ");
      else if (msg.includes("AMBIGUOUS_OEM_MATCH"))
        toast.error("ພົບ OEM ຫຼາຍລາຍການ — ເລືອກຈາກລາຍການກ່ອນ");
      else if (msg.includes("INVALID_CATEGORY"))
        toast.error("ໝວດສິນຄ້າບໍ່ຖືກຕ້ອງ — ເລືອກໃໝ່");
      else toast.error(msg);
    },
  });
}

export const shopProductClaimsKeys = {
  all: (shopId: string) => ["shops", shopId, "product-claims"] as const,
  list: (shopId: string, q: ShopProductClaimListQueryDTO) =>
    [...shopProductClaimsKeys.all(shopId), "list", q] as const,
  detail: (shopId: string, claimId: string) =>
    [...shopProductClaimsKeys.all(shopId), "detail", claimId] as const,
};

export const shopWarrantiesKeys = {
  all: (shopId: string) => ["shops", shopId, "warranties"] as const,
  expiringSoon: (shopId: string, q: ShopWarrantiesExpiringQueryDTO) =>
    [...shopWarrantiesKeys.all(shopId), "expiring-soon", q] as const,
};

export function useShopWarrantiesExpiringSoonQuery(
  shopId: string | undefined,
  query: ShopWarrantiesExpiringQueryDTO | undefined,
) {
  return useQuery({
    queryKey:
      shopId && query
        ? shopWarrantiesKeys.expiringSoon(shopId, query)
        : ["shops", "__", "warranties", "expiring-soon", "disabled"],
    queryFn: () => {
      if (!shopId || !query?.branchId)
        throw new Error("shopId and branchId required");
      return shopWarrantiesApi.listExpiringSoon(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId,
  });
}

export function useShopProductClaimsListQuery(
  shopId: string | undefined,
  query: ShopProductClaimListQueryDTO | undefined,
) {
  return useQuery({
    queryKey:
      shopId && query
        ? shopProductClaimsKeys.list(shopId, query)
        : ["shops", "__", "product-claims", "list", "disabled"],
    queryFn: () => {
      if (!shopId || !query?.branchId)
        throw new Error("shopId and branchId required");
      return shopProductClaimsApi.list(shopId, query);
    },
    enabled: !!shopId && !!query?.branchId,
  });
}

export function useShopProductClaimDetailQuery(
  shopId: string | undefined,
  claimId: string | undefined,
) {
  return useQuery({
    queryKey: shopProductClaimsKeys.detail(shopId ?? "", claimId ?? ""),
    queryFn: () => {
      if (!shopId || !claimId) throw new Error("shopId and claimId required");
      return shopProductClaimsApi.get(shopId, claimId);
    },
    enabled: !!shopId && !!claimId,
  });
}

export function useCreateShopProductClaim(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShopProductClaimDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.create(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
      }
      toast.success("ສ້າງເຄສສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ສ້າງເຄສລົ້ມເຫຼວ");
    },
  });
}

export function useUpdateShopProductClaimDraft(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      claimId: string;
      body: UpdateShopProductClaimDraftDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.updateDraft(shopId, args.claimId, args.body);
    },
    onSuccess: (_data, args) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, args.claimId),
        });
      }
      toast.success("ບັນທຶກຮ່າງແລ້ວ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
    },
  });
}

export function useSubmitShopProductClaim(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.submit(shopId, claimId);
    },
    onSuccess: (_data, claimId) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, claimId),
        });
      }
      toast.success("ສົ່ງເຄສແລ້ວ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ສົ່ງບໍ່ສຳເລັດ");
    },
  });
}

export function useCancelShopProductClaim(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (claimId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.cancel(shopId, claimId);
    },
    onSuccess: (_data, claimId) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, claimId),
        });
      }
      toast.success("ຍົກເລີກເຄສແລ້ວ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ຍົກເລີກບໍ່ສຳເລັດ");
    },
  });
}

export function useSetShopProductClaimStatus(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      claimId: string;
      body: SetShopProductClaimStatusDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.setStatus(shopId, args.claimId, args.body);
    },
    onSuccess: (_data, args) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, args.claimId),
        });
      }
      toast.success("ບັນທຶກສະຖານະແລ້ວ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ບັນທຶກບໍ່ສຳເລັດ");
    },
  });
}

export function useAddShopProductClaimAttachment(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      claimId: string;
      body: AddShopProductClaimAttachmentDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.addAttachment(
        shopId,
        args.claimId,
        args.body,
      );
    },
    onSuccess: (_data, args) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, args.claimId),
        });
      }
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ອັບໂຫຼດບໍ່ສຳເລັດ");
    },
  });
}

export function useRemoveShopProductClaimAttachment(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { claimId: string; attachmentId: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopProductClaimsApi.removeAttachment(
        shopId,
        args.claimId,
        args.attachmentId,
      );
    },
    onSuccess: (_data, args) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopProductClaimsKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopProductClaimsKeys.detail(shopId, args.claimId),
        });
      }
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ລຶບບໍ່ສຳເລັດ");
    },
  });
}

export const purchaseOrdersKeys = {
  all: (shopId: string) => ["shops", shopId, "purchase-orders"] as const,
  list: (shopId: string, q: PurchaseOrderListQueryDTO) =>
    [...purchaseOrdersKeys.all(shopId), "list", q] as const,
  detail: (shopId: string, purchaseOrderId: string) =>
    [...purchaseOrdersKeys.all(shopId), "detail", purchaseOrderId] as const,
  receipts: (shopId: string, purchaseOrderId: string) =>
    [...purchaseOrdersKeys.all(shopId), "receipts", purchaseOrderId] as const,
};

export const suppliersKeys = {
  all: (shopId: string) => ["shops", shopId, "suppliers"] as const,
  list: (shopId: string, q: SupplierListQueryDTO) =>
    [...suppliersKeys.all(shopId), "list", q] as const,
};

export function useSuppliersListQuery(
  shopId: string | undefined,
  query: SupplierListQueryDTO,
) {
  return useQuery({
    queryKey: suppliersKeys.list(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopSuppliersApi.list(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function useCreateSupplierMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateSupplierDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopSuppliersApi.create(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: suppliersKeys.all(shopId) });
      }
    },
  });
}

export function useUpdateSupplierMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { supplierId: string; body: UpdateSupplierDTO }) => {
      if (!shopId) throw new Error("shopId required");
      return shopSuppliersApi.update(shopId, args.supplierId, args.body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: suppliersKeys.all(shopId) });
      }
    },
  });
}

export const shopPromotionsKeys = {
  all: (shopId: string) => ["shops", shopId, "promotions"] as const,
  list: (shopId: string, q: ShopPromotionListQueryDTO) =>
    [...shopPromotionsKeys.all(shopId), "list", q] as const,
  detail: (shopId: string, promotionId: string) =>
    [...shopPromotionsKeys.all(shopId), "detail", promotionId] as const,
};

export function useShopPromotionsListQuery(
  shopId: string | undefined,
  query: ShopPromotionListQueryDTO,
) {
  return useQuery({
    queryKey: shopPromotionsKeys.list(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPromotionsApi.list(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function useShopPromotionDetailQuery(
  shopId: string | undefined,
  promotionId: string | undefined,
) {
  return useQuery({
    queryKey: shopPromotionsKeys.detail(shopId ?? "", promotionId ?? ""),
    queryFn: () => {
      if (!shopId || !promotionId) throw new Error("ids required");
      return shopPromotionsApi.get(shopId, promotionId);
    },
    enabled: !!shopId && !!promotionId,
  });
}

export function useCreateShopPromotionMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateShopPromotionDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopPromotionsApi.create(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopPromotionsKeys.all(shopId) });
      }
    },
  });
}

export function useUpdateShopPromotionMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      promotionId: string;
      body: UpdateShopPromotionDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPromotionsApi.update(shopId, args.promotionId, args.body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopPromotionsKeys.all(shopId) });
      }
    },
  });
}

export function usePurchaseOrdersListQuery(
  shopId: string | undefined,
  query: PurchaseOrderListQueryDTO,
) {
  return useQuery({
    queryKey: purchaseOrdersKeys.list(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.list(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function usePurchaseOrderDetailQuery(
  shopId: string | undefined,
  purchaseOrderId: string | undefined,
) {
  return useQuery({
    queryKey: purchaseOrdersKeys.detail(shopId ?? "", purchaseOrderId ?? ""),
    queryFn: () => {
      if (!shopId || !purchaseOrderId) throw new Error("ids required");
      return shopPurchaseOrdersApi.get(shopId, purchaseOrderId);
    },
    enabled: !!shopId && !!purchaseOrderId,
  });
}

/** P2: ປະຫວັດໃບຮັບ (GRN) ຕໍ່ PO — ປິດເມື່ອຍັງເປັນຮ່າງ */
export function usePurchaseOrderReceiptsQuery(
  shopId: string | undefined,
  purchaseOrderId: string | undefined,
  options?: { enabled?: boolean },
) {
  const enabledByStatus = options?.enabled ?? true;
  return useQuery({
    queryKey: purchaseOrdersKeys.receipts(shopId ?? "", purchaseOrderId ?? ""),
    queryFn: () => {
      if (!shopId || !purchaseOrderId) throw new Error("ids required");
      return shopPurchaseOrdersApi.listReceipts(shopId, purchaseOrderId);
    },
    enabled: !!shopId && !!purchaseOrderId && enabledByStatus,
  });
}

export function useCreatePurchaseOrderMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePurchaseOrderDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.create(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all(shopId) });
      }
    },
  });
}

export function useUpdatePurchaseOrderMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      purchaseOrderId,
      body,
    }: {
      purchaseOrderId: string;
      body: UpdatePurchaseOrderDTO;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.update(shopId, purchaseOrderId, body);
    },
    onSuccess: (_data, vars) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: purchaseOrdersKeys.detail(shopId, vars.purchaseOrderId),
        });
      }
    },
  });
}

export function useSubmitPurchaseOrderMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseOrderId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.submit(shopId, purchaseOrderId);
    },
    onSuccess: (_data, purchaseOrderId) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: purchaseOrdersKeys.detail(shopId, purchaseOrderId),
        });
      }
    },
  });
}

export function useCancelPurchaseOrderMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (purchaseOrderId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.cancel(shopId, purchaseOrderId);
    },
    onSuccess: (_data, purchaseOrderId) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: purchaseOrdersKeys.detail(shopId, purchaseOrderId),
        });
      }
    },
  });
}

export function useReceivePurchaseOrderGoodsMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      purchaseOrderId: string;
      body: PurchaseOrderGoodsReceiptDTO;
      idempotencyKey?: string;
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopPurchaseOrdersApi.receiveGoods(
        shopId,
        args.purchaseOrderId,
        args.body,
        args.idempotencyKey,
      );
    },
    onSuccess: (_data, vars) => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopInventoryKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: shopCatalogKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: purchaseOrdersKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: purchaseOrdersKeys.detail(shopId, vars.purchaseOrderId),
        });
        qc.invalidateQueries({
          queryKey: purchaseOrdersKeys.receipts(shopId, vars.purchaseOrderId),
        });
      }
      toast.success("ບັນທຶກຮັບເຂົ້າສຳເລັດ");
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : "ຮັບເຂົ້າລົ້ມເຫຼວ");
    },
  });
}

export const shopMarketplaceKeys = {
  all: (shopId: string) => ["shops", shopId, "marketplace"] as const,
  settings: (shopId: string) =>
    [...shopMarketplaceKeys.all(shopId), "settings"] as const,
  list: (shopId: string, q: ShopMarketplaceListQueryDTO) =>
    [...shopMarketplaceKeys.all(shopId), "list", q] as const,
};

export function useShopMarketplaceSettingsQuery(shopId: string | undefined) {
  return useQuery({
    queryKey: shopMarketplaceKeys.settings(shopId ?? ""),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.getSettings(shopId);
    },
    enabled: !!shopId,
  });
}

export function useUpdateShopMarketplaceSettingsMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { autoPublish: boolean }) => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.updateSettings(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopMarketplaceKeys.all(shopId) });
        qc.invalidateQueries({ queryKey: ["merchant", "shops"] });
        qc.invalidateQueries({
          queryKey: shopReportsKeys.moduleStats(shopId),
        });
      }
    },
  });
}

export function useSyncMarketplaceMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.syncAll(shopId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopMarketplaceKeys.all(shopId) });
      }
    },
  });
}

export function useShopMarketplaceListQuery(
  shopId: string | undefined,
  query: ShopMarketplaceListQueryDTO,
) {
  return useQuery({
    queryKey: shopMarketplaceKeys.list(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.list(shopId, query);
    },
    enabled: !!shopId,
  });
}

export function usePublishMarketplaceListingMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PublishMarketplaceListingDTO) => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.publish(shopId, body);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopMarketplaceKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopReportsKeys.moduleStats(shopId),
        });
      }
    },
  });
}

export function useUnpublishMarketplaceListingMutation(
  shopId: string | undefined,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listingId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopMarketplaceApi.unpublish(shopId, listingId);
    },
    onSuccess: () => {
      if (shopId) {
        qc.invalidateQueries({ queryKey: shopMarketplaceKeys.all(shopId) });
        qc.invalidateQueries({
          queryKey: shopReportsKeys.moduleStats(shopId),
        });
      }
    },
  });
}

// --- ບໍລິການ (service) CRUD ---
export const shopServicesKeys = {
  all: (shopId: string) => ["shops", shopId, "services"] as const,
  list: (shopId: string, q: { q?: string; limit?: number; offset?: number }) =>
    [...shopServicesKeys.all(shopId), "list", q] as const,
};

export function useShopServicesQuery(
  shopId: string | undefined,
  query: { q?: string; limit?: number; offset?: number },
) {
  return useQuery({
    queryKey: shopServicesKeys.list(shopId ?? "", query),
    queryFn: () => {
      if (!shopId) throw new Error("shopId required");
      return shopServicesApi.list(shopId, query);
    },
    enabled: Boolean(shopId),
  });
}

function invalidateServices(
  qc: ReturnType<typeof useQueryClient>,
  shopId: string | undefined,
) {
  if (!shopId) return;
  void qc.invalidateQueries({ queryKey: shopServicesKeys.all(shopId) });
  // labor presets (workshop) read active services — refetch them too
  void qc.invalidateQueries({ queryKey: ["shops", shopId, "workshop"] });
}

function serviceErrorMessage(err: unknown, fallback: string) {
  return err instanceof Error && err.message ? err.message : fallback;
}

export function useCreateServiceMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; price: number; currency?: string }) => {
      if (!shopId) throw new Error("shopId required");
      return shopServicesApi.create(shopId, body);
    },
    onSuccess: () => {
      invalidateServices(qc, shopId);
      toast.success("ເພີ່ມບໍລິການແລ້ວ");
    },
    onError: (err) => toast.error(serviceErrorMessage(err, "ເພີ່ມບໍລິການບໍ່ໄດ້")),
  });
}

export function useUpdateServiceMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      shopProductSkuId: string;
      body: {
        name?: string;
        price?: number;
        currency?: string;
        isActive?: boolean;
      };
    }) => {
      if (!shopId) throw new Error("shopId required");
      return shopServicesApi.update(shopId, vars.shopProductSkuId, vars.body);
    },
    onSuccess: () => {
      invalidateServices(qc, shopId);
      toast.success("ບັນທຶກບໍລິການແລ້ວ");
    },
    onError: (err) => toast.error(serviceErrorMessage(err, "ບັນທຶກບໍລິການບໍ່ໄດ້")),
  });
}

export function useDeleteServiceMutation(shopId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (shopProductSkuId: string) => {
      if (!shopId) throw new Error("shopId required");
      return shopServicesApi.remove(shopId, shopProductSkuId);
    },
    onSuccess: () => {
      invalidateServices(qc, shopId);
      toast.success("ລຶບບໍລິການແລ້ວ");
    },
    onError: (err) => toast.error(serviceErrorMessage(err, "ລຶບບໍລິການບໍ່ໄດ້")),
  });
}
